import { esc } from "../ui/format.js";
import { uiCard, uiGet, uiHeader, uiKpis, uiMoney } from "../ui/kit.js";
import { emAct, emLetters, emLine, emLog, emWireLetters } from "./empire.js";

/**
 * Climate & ESG (backend/esg.py): your emissions against the baseline, the
 * carbon price your industry pays, the ESG score (Environment, Social,
 * Governance) and its history, green projects to build, and the letters
 * from regulators and green investors.
 */

function esEmissions(e) {
  const W = 640, H = 90, max = Math.max(1, e.baseline_t);
  const w = (v) => ((W - 40) * v) / max;
  return `<svg class="em-line" viewBox="0 0 ${W} ${H}" role="img" aria-label="Emissions ${Math.round(e.emissions_t)} tonnes of ${Math.round(e.baseline_t)}">
    <text x="20" y="16" class="em-axis">Without abatement</text>
    <rect x="20" y="22" width="${w(e.baseline_t).toFixed(1)}" height="18" rx="6" fill="rgba(251,113,133,.35)"/>
    <text x="${(24 + w(e.baseline_t)).toFixed(1)}" y="36" class="em-axis" text-anchor="end">${Math.round(e.baseline_t).toLocaleString("en-US")} t</text>
    <text x="20" y="58" class="em-axis">You now</text>
    <rect x="20" y="64" width="${Math.max(4, w(e.emissions_t)).toFixed(1)}" height="18" rx="6" fill="rgba(52,211,153,.7)"/>
    <text x="${(28 + Math.max(4, w(e.emissions_t))).toFixed(1)}" y="78" class="em-axis">${Math.round(e.emissions_t).toLocaleString("en-US")} t a month (−${e.abatement_pct}%)</text>
  </svg>`;
}

function esScore(sc) {
  const bar = (label, v, tone) => `<div class="em-bar-row"><span>${label}</span><div class="em-bar"><i style="width:${v}%;background:${tone}"></i></div><b>${Math.round(v)}</b></div>`;
  return `<div class="es-score"><div class="es-grade">${esc(sc.grade)}<small>${Math.round(sc.score)}/100</small></div>
    <div class="em-bars">${bar("Environment (50%)", sc.environment, "var(--sage)")}${bar("Social (25%)", sc.social, "var(--neon-cyan)")}${bar("Governance (25%)", sc.governance, "var(--neon-violet)")}</div></div>`;
}

async function renderEsg(main) {
  const e = await uiGet("/esg");
  main.innerHTML = `
    <div class="sx em">
      ${uiHeader(`Climate & ESG — Month ${e.month}`, e.covered ? "Your industry is a heavy emitter: you pay for every tonne, and the price only goes up. Cut emissions before they cut you." : "Your industry pays no carbon tax, but investors, customers and regulators still read your ESG score.")}
      ${uiKpis([
        { label: "ESG score", value: `${e.score.grade}`, sub: `${Math.round(e.score.score)}/100`, tone: e.score.score >= 65 ? "pos" : e.score.score < 40 ? "neg" : "" },
        { label: "Emissions", value: `${Math.round(e.emissions_t).toLocaleString("en-US")} t`, sub: `a month · ${e.intensity_kg_per_usd} kg per $` },
        { label: "Carbon price", value: e.covered ? (e.carbon_price ? `$${Math.round(e.carbon_price)}/t` : `from M${e.carbon_starts_month}`) : "None", sub: e.covered ? `≈ ${uiMoney(e.carbon_tax_month, { compact: true })} a month` : "not your industry" },
        { label: "Carbon tax paid", value: uiMoney(e.taxes_paid, { compact: true }), sub: "so far" },
        { label: "Abatement", value: `${e.abatement_pct}%`, sub: e.pledge ? `pledged ${e.pledge.target_pct}% by M${e.pledge.due}` : "of emissions cut" },
      ])}
      ${e.open.length ? uiCard("Waiting for you", emLetters(e.open)) : ""}
      <div class="em-grid">
        ${uiCard("Your ESG score", esScore(e.score), { sub: e.rules })}
        ${uiCard("Emissions", esEmissions(e), { sub: `${e.offsets_until ? `Offsets active until M${e.offsets_until}. ` : ""}${e.report_until ? `Sustainability report valid until M${e.report_until}.` : ""}` })}
      </div>
      ${uiCard("ESG score over time", emLine(e.history.map((h) => ({ month: h.month, value: h.score })), { money: false, label: "ESG score" }))}
      ${uiCard("Green projects", `<div class="em-cards">${e.projects.map((p) => `
        <div class="em-tile"><div class="em-tile-icon">${p.icon}</div><b>${esc(p.name)}</b><span>${esc(p.what)}</span>
          <em>Cuts about ${p.cut_pct}% of what's left · ${uiMoney(p.cost)}</em>
          <button type="button" class="primary" data-es-proj="${esc(p.key)}" ${e.abatement_pct >= 85 ? "disabled" : ""}>Build it</button></div>`).join("")}
        <div class="em-tile"><div class="em-tile-icon">📑</div><b>Audited sustainability report</b><span>Publish your numbers. Governance +5 for a year (a weak score risks a greenwashing story).</span>
          <em>$40,000</em><button type="button" data-es-report>Publish</button></div></div>`,
        { sub: "Projects are capital spending: they go on the balance sheet and depreciate, like any equipment." })}
      <div class="em-grid">
        ${uiCard("Projects built", e.built.length ? `<ol class="em-log">${e.built.map((b) => `<li><b>M${b.month}</b><span>${esc(b.name)}: ${uiMoney(b.cost, { compact: true })}, −${b.cut_pts} pts of emissions</span></li>`).join("")}</ol>` : `<p class="sx-card-sub">None yet. Each green project above cuts your emissions for good and lowers the carbon tax you pay.</p>`)}
        ${uiCard("What happened", emLog(e.log.length ? e.log : e.history_letters, { emptyText: "No regulators or green funds have written yet." }))}
      </div>
    </div>`;
  emWireLetters(main);
  main.querySelectorAll("[data-es-proj]").forEach((b) => b.addEventListener("click", () => {
    const p = e.projects.find((x) => x.key === b.dataset.esProj);
    if (confirm(`Build ${p.name} for ${uiMoney(p.cost)}?`)) emAct("esg_green_project", { key: p.key });
  }));
  main.querySelector("[data-es-report]").addEventListener("click", () => emAct("esg_report"));
}

window.renderEsg = renderEsg;

export { renderEsg };
